import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { connect } from "react-redux";
import UsersTable from "./users_by_group_table.jsx";
import AssignUsers from "./assign_users/assign_users.jsx";
import { useModalSelectUsers } from "../../../hooks/useModalSelectUsers.js";
import {
  addGroup,
  forgetUsers,
  getAllGroups,
  getGroup,
  updateGroup,
} from "../../../actions/manageGroupsActions.js";
import confirm from "../../../components/ConfirmModal/confirm.js";
import addUsersIcon from "../../../assets/icons/AddUsersIcon.png";
import styles from "./group_form.module.css";
import alert from "../../../components/AlertModal/alertModal.js";
import {
  openLoading,
  closeLoading,
} from "../../../components/loadingModal/loading.js";
import verifyText from "../../../util/validations/text.js";
import Button from "../../../components/Button/index.jsx";
import InputText from "../../../components/InputText/index.jsx";
import editIcon from "../../../assets/icons/lapizBlue2.png";
import ArrowGoBack from "../../../components/ArrowGoBack/ArrowGoBack.jsx";

const CreateOrEditGroup = (props) => {
  const {
    users = [],
    group = {},
    forgetCurrentData,
    getAllGroups,
    addGroup,
    getGroup,
    updateGroup,
  } = props;

  const { groupId } = useParams();
  const navigate = useNavigate();
  const [isOpen, openModal, closeModal] = useModalSelectUsers(false);
  const [groupName, setGroupName] = useState("");
  const [editTitle, setEditTitle] = useState(true);
  const [errors, setErrors] = useState([]);
  const [userError, setUserError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isCreating = !groupId;

  // Initialize component
  useEffect(() => {
    forgetCurrentData();

    if (groupId) {
      getGroup(groupId).then((res) => {
        setGroupName(res.name || "");
      });
      setEditTitle(false);
    }

    return () => {
      getAllGroups({ column: "name", order: "asc", atribute: "groups" });
    };
  }, [groupId, forgetCurrentData, getGroup, getAllGroups]);

  // Handlers optimizados con useCallback
  const handleInput = useCallback(
    (e) => {
      setGroupName(e.target.value);
      // Clear errors when user starts typing
      if (errors.length > 0) {
        setErrors([]);
      }
      if (userError) {
        setUserError(false);
      }
    },
    [errors.length, userError],
  );

  const handleEditName = useCallback((e) => {
    e?.preventDefault();
    setEditTitle(true);
  }, []);

  const handleOpenAddUsers = useCallback(
    (e) => {
      e?.preventDefault();
      openModal();
    },
    [openModal],
  );

  const handleCancel = useCallback(() => {
    navigate("/manage-groups");
  }, [navigate]);

  // Validación común
  const validateForm = useCallback(() => {
    const formErrors = verifyText({ groupName });
    const hasUserError = isCreating && users.length === 0;

    setErrors(formErrors);
    setUserError(hasUserError);

    return formErrors.length === 0 && !hasUserError;
  }, [groupName, users.length, isCreating]);

  // Submit común
  const handleSubmit = useCallback(
    async (isCreate) => {
      if (!validateForm()) return;

      const action = isCreate ? "create" : "edit";
      const actionText = isCreate ? "created" : "edited";

      try {
        const confirmation = await confirm(
          "CONFIRM",
          `Are you sure you want to ${action} the group <strong>${groupName}</strong>?`,
        );

        if (!confirmation) return;

        setIsSubmitting(true);
        openLoading();

        if (isCreate) {
          await addGroup({
            name: groupName,
            usersGroup: users,
            id: groupId,
          });
        } else {
          await updateGroup({
            name: groupName,
            idGroup: groupId,
          });
        }

        navigate("/manage-groups");
        alert("SUCCESS", `Group has been ${actionText}`);
      } catch (error) {
        alert("ERROR", error.message);
      } finally {
        setIsSubmitting(false);
        closeLoading();
      }
    },
    [validateForm, groupName, users, groupId, addGroup, updateGroup, navigate],
  );

  const handleCreate = useCallback(
    (e) => {
      e?.preventDefault();
      handleSubmit(true);
    },
    [handleSubmit],
  );

  const handleEdit = useCallback(
    (e) => {
      e?.preventDefault();
      handleSubmit(false);
    },
    [handleSubmit],
  );

  // Datos derivados
  const currentCourses = group?.courses || [];
  const hasCourses = currentCourses.length > 0;
  const hasUsers = users.length > 0;
  const nameErrors = errors.filter((error) => error.label === "groupName");

  return (
    <div className="box-container">
      <ArrowGoBack />
      <div className={`group-box-entry ${styles.container}`}>
        <div className={styles.wrapper}>
          {/* Header Section */}
          <div className={styles.header}>
            <div className={styles.headerMain}>
              <button
                className="close"
                onClick={handleCancel}
                onKeyDown={(e) => e.key === "Enter" && handleCancel()}
              />
              <div className={styles.titleSection}>
                {editTitle ? (
                  <div className={styles.inputContainer}>
                    <InputText
                      type="text"
                      placeholder="Group Name"
                      name="groupName"
                      onChange={handleInput}
                      value={groupName}
                      errors={nameErrors}
                      autoFocus
                    />
                  </div>
                ) : (
                  <div className={styles.titleDisplay}>
                    <h1 className={styles.title}>{group.name}</h1>
                    <button
                      className={styles.editButton}
                      onClick={handleEditName}
                      aria-label="Edit group name"
                    >
                      <img src={editIcon} alt="Edit" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Associated Courses */}
            {hasCourses && (
              <div className={styles.coursesSection}>
                <span className={styles.coursesLabel}>ASSOCIATED COURSES</span>
                <div className={styles.coursesList}>
                  {currentCourses.map((course, index) => (
                    <span key={course.id || index} className={styles.courseTag}>
                      {course.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions Section */}
          <div className={styles.actions}>
            <Button
              size="medium"
              styleButton="main"
              label="ADD USERS"
              onClick={handleOpenAddUsers}
              disabled={isSubmitting}
              image={
                <img
                  src={addUsersIcon}
                  alt="Add Users"
                  className={styles.btnIcon}
                />
              }
            />
          </div>

          {/* Content Area */}
          <div className={styles.content}>
            {hasUsers ? (
              <div className={styles.tableContainer}>
                <UsersTable users={users} />
              </div>
            ) : userError ? (
              <div className={styles.errorState}>
                <div className={styles.errorIcon}>⚠️</div>
                <div className={styles.errorMessage}>
                  You must add users to the group before saving.
                </div>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>👥</div>
                <div className={styles.emptyText}>
                  No users added yet. Click "ADD USERS" to get started.
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <Button
              label={isSubmitting ? "SAVING..." : "SAVE"}
              size="medium"
              styleButton="main"
              onClick={isCreating ? handleCreate : handleEdit}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Assign Users Modal */}
        {isOpen && <AssignUsers closeModal={closeModal} />}
      </div>
    </div>
  );
};

// MapDispatchToProps simplificado
const mapDispatchToProps = {
  getGroup: (groupId) => getGroup(groupId),
  forgetCurrentData: forgetUsers,
  getAllGroups: (data) => getAllGroups(data),
  addGroup: (data) => addGroup(data),
  updateGroup: (data) => updateGroup(data),
};

// MapStateToProps con valores por defecto
const mapStateToProps = (state) => ({
  group: state.manageGroups?.groupData || {},
  users: state.manageGroups?.users || [],
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateOrEditGroup);
